/* global $, API */

import { Controller } from 'stimulus';
import toArray from '../helpers/toArray';
import dataLayerHelper from '../helpers/dataLayer';

class QuestionnaireController extends Controller {
  connect() {
    this.summary = [];
    this.currentQuestionIndex = 0;
  }

  onStartClick() {
    this.questionnaireTarget.classList.toggle('is-open');
  }

  onStartOverClick() {
    this.startOver();
  }

  onPreviousQuestionTogglerClick() {
    this.resetQuestions(this.currentQuestionIndex - 1);
  }

  onAnswerChange(e) {
    this.toggleNextQuestion(e.currentTarget);
  }

  onHelpButtonClick() {
    this.toggleHelp();
  }

  // eslint-disable-next-line class-methods-use-this
  onResultClick(e) {
    dataLayerHelper.event(
      'Keuzehulp',
      'Product gekozen',
      e.currentTarget.closest('[data-target="questionnaire.resultsItem"]').dataset.resultTitle,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  onResultVariantClick(e) {
    dataLayerHelper.event(
      'Keuzehulp',
      'Variant gekozen',
      e.currentTarget.dataset.variantTitle,
    );
  }

  startOver() {
    this.togglePreviousQuestionToggler(false);
    this.toggleResults(false);
    this.renderResults('');
    this.resetQuestions(0);

    this.formTarget.style.display = 'none';
    this.resultsTitleTarget.style.display = 'none';
    this.noResultsTitleTarget.style.display = 'none';
    this.informationPageTarget.style.display = 'none';
  }

  toggleNextQuestion(answer) {
    const question = answer.closest('[data-target="questionnaire.question"]');
    const questionAnswers = toArray(question.querySelectorAll('[data-target="questionnaire.answer"]'));
    const nextQuestion = parseInt(answer.dataset.nextQuestion, 10);
    const isQuestionAnswered = questionAnswers
      .filter(questionAnswer => questionAnswer.checked)
      .length;

    this.currentQuestionIndex = toArray(this.questionsTarget.children).indexOf(question) + 1;

    if (isQuestionAnswered) {
      this.summary.push(answer.dataset.text);
      question.classList.add('has-been-answered');
      question.classList.remove('is-active');
    }

    this.togglePreviousQuestionToggler(true, this.currentQuestionIndex);

    if (!nextQuestion) {
      const selectedAnswers = this.answerTargets
        .filter(selectedAnswer => selectedAnswer.checked)
        .map(selectedAnswer => ({
          questionIdentifier: selectedAnswer.dataset.questionIdentifier,
          answerValue: selectedAnswer.value,
          analyticsTag: selectedAnswer.dataset.analyticsTag,
        }));

      dataLayerHelper.event(
        'Keuzehulp',
        'Vragen beantwoord',
        selectedAnswers.map(selectedAnswer => selectedAnswer.analyticsTag).join(),
      );

      this.fetchResults(selectedAnswers);
      return;
    }

    this.fetchNextQuestion(nextQuestion);
  }

  fetchNextQuestion(nextQuestion) {
    $.ajax({
      type: 'POST',
      url: API.questionnaire,
      dataType: 'json',
      data: {
        nextQuestion,
      },
      success: res => this.renderQuestion(res.question),
    });
  }

  fetchResults(answers) {
    $.ajax({
      type: 'POST',
      url: API.questionnaireResults,
      dataType: 'json',
      data: {
        answers,
      },
      success: (res) => {
        this.renderResults(res.results);
        this.renderSummary(res.meta.summary);
        this.outputResultIds(res.meta.productIds);
        this.renderLargerBoilerContent(
          res.meta.showLargerBoilerText,
          res.meta.largerBoilerCategoryUrl,
        );
        this.renderInformationPage(res.informationUrlPage);
      },
    });
  }

  outputResultIds(ids) {
    if (!ids) return;

    this.resultsHiddenInputTarget.value = ids;
  }

  renderSummary(text) {
    this.summaryTarget.innerHTML = text;
  }

  renderLargerBoilerContent(showContent, categoryUrl) {
    if (!showContent) return;

    this.largerBoilerText.style.display = 'block';
    this.largerBoilerLink.href = categoryUrl;
  }

  renderResults(results) {
    this.toggleResults(results.length > 0);
    this.resultsOutputTarget.innerHTML = results;
  }

  renderQuestion(question) {
    const questionTemplate = document.createElement('div');
    questionTemplate.classList.add('questionnaire__question-wrapper', 'is-active');
    questionTemplate.setAttribute('data-target', 'questionnaire.question');
    questionTemplate.innerHTML = question;

    this.questionsTarget.appendChild(questionTemplate);
  }

  renderInformationPage(text) {
    this.informationPageTarget.style.display = 'block';
    this.informationPageTarget.innerHTML = text;
  }

  toggleResults(hasResults) {
    this.resultsTarget.classList.add('is-visible');

    if (!hasResults) {
      this.noResultsTitleTarget.style.display = 'block';
      return;
    }

    this.formTarget.style.display = 'block';
    this.resultsTitleTarget.style.display = 'block';
  }

  togglePreviousQuestionToggler(isVisible, questionNumber = '0') {
    if (!isVisible) {
      this.previousQuestionTogglerTarget.classList.remove('is-visible');
      return;
    }

    this.previousQuestionTogglerTarget.classList.add('is-visible', isVisible);
    this.previousQuestionTogglerTarget.querySelector('span').innerText = questionNumber;
  }

  toggleHelp() {
    this.helpTarget.classList.toggle('is-open');
  }

  resetQuestions(newQuestionIndex) {
    this.currentQuestionIndex = newQuestionIndex;
    this.resultsTarget.classList.remove('is-visible');
    this.resultsOutputTarget.innerHTML = '';

    const currentQuestion = this.questionTargets[this.currentQuestionIndex];
    const nextQuestions = this.questionTargets.slice(this.currentQuestionIndex + 1);

    nextQuestions.forEach((nextStep) => { nextStep.remove(); });

    currentQuestion.classList.add('is-active');
    currentQuestion.classList.remove('has-been-answered');

    toArray(currentQuestion.querySelectorAll('[data-target="questionnaire.answer"]'))
      .forEach((answer) => { answer.checked = false; });

    this.togglePreviousQuestionToggler(this.currentQuestionIndex > 0, this.currentQuestionIndex);
  }
}

QuestionnaireController.targets = [
  'questionnaire',
  'questions',
  'question',
  'answer',
  'results',
  'help',
  'form',
  'summary',
  'resultsTitle',
  'noResultsTitle',
  'largerBoilerLink',
  'largerBoilerText',
  'resultsOutput',
  'resultsHiddenInput',
  'previousQuestionToggler',
  'informationPage',
];

export default QuestionnaireController;
